/**
 * @file useAttachmentPanelDrop.ts
 * @description Native drag-and-drop for the whole attachments panel: a file
 * dropped anywhere attaches, one dropped on a row versions that row. Consumer:
 * `Attachments`, which spreads `containerHandlers`, fences the upload strip with
 * `onDraggerBoundaryDrop` and gives `AttachmentList` the row handlers.
 *
 * Depths are refs: `dragenter`/`dragleave` bubble, so only a depth back at zero
 * means the pointer left, and a discrete `drop` never flushes the `dragenter`
 * that set `dropTargetFileUid`. Flipping `enabled` resets both during render.
 */
import { DragEvent, DragEventHandler, useCallback, useRef, useState } from 'react';

/**
 * Whether a drag carries files. `dataTransfer.items` is not readable while a
 * drag is in flight in every browser, but `types` always is, so text, links and
 * dragged table rows are told apart from files here and light nothing up.
 */
const carriesFiles = (event: DragEvent<HTMLElement>): boolean =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

/**
 * The files a release actually carries. `files` can be empty while `items`
 * still holds entries of kind `file`, which rc-upload never uploads from, and
 * `getAsFile()` reads them synchronously — unlike `webkitGetAsEntry()`, which
 * a drop handler cannot wait for.
 */
const releasedFiles = (event: DragEvent<HTMLElement>): File[] => {
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) return files;
    return Array.from(event.dataTransfer?.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
};

export interface AttachmentPanelDropOptions {
    /** False for a read-only or busy panel, which then registers no handler. */
    enabled: boolean;
    /**
     * Receives the dropped files and the file row the pointer was over, or null
     * when the drop landed on the panel background. Never called with an empty
     * list — that release goes to `onEmptyDrop`.
     */
    onFilesDropped: (files: File[], dropTargetFileUid: string | null) => void;
    /**
     * A release that advertised files and carried none in `files` or `items`,
     * on either surface. rc-upload is silent about it, so the panel is the only
     * thing that can tell the reader the drag arrived empty.
     */
    onEmptyDrop?: () => void;
}

export interface AttachmentPanelDropContainerHandlers {
    onDragEnter?: DragEventHandler<HTMLElement>;
    onDragOver?: DragEventHandler<HTMLElement>;
    onDragLeave?: DragEventHandler<HTMLElement>;
    onDrop?: DragEventHandler<HTMLElement>;
}

export interface AttachmentPanelDrop {
    /** Whether a file drag is currently over the panel. */
    isDragging: boolean;
    /** The file row the pointer is over, or null for the panel background. */
    dropTargetFileUid: string | null;
    /** Spread on the panel's outermost element. */
    containerHandlers: AttachmentPanelDropContainerHandlers;
    /**
     * Spread on the element fencing the upload strip from the container, so the
     * container never uploads a drop the strip already took. It still does the
     * bookkeeping the stopped drop would have done — ending the drag, and
     * reporting a release that carried no file — and prevents the browser
     * default, which the strip's own handler never sees when the release lands
     * on the fence itself. It uploads only a release holding its file in
     * `items` alone, which the strip never reads.
     */
    onDraggerBoundaryDrop?: DragEventHandler<HTMLElement>;
    /** Called by a row that can take a version, from its own `dragenter`. */
    onRowDragEnter?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
    /** Called by the same row from its own `dragleave`. */
    onRowDragLeave?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
}

export const useAttachmentPanelDrop = ({
    enabled,
    onFilesDropped,
    onEmptyDrop,
}: AttachmentPanelDropOptions): AttachmentPanelDrop => {
    const dragDepth = useRef(0);
    const rowDragDepths = useRef(new Map<string, number>());
    const dropTargetFileUidRef = useRef<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dropTargetFileUid, setDropTargetFileUid] = useState<string | null>(null);
    const [enabledLastRender, setEnabledLastRender] = useState(enabled);

    if (enabledLastRender !== enabled) {
        setEnabledLastRender(enabled);
        dragDepth.current = 0;
        rowDragDepths.current.clear();
        dropTargetFileUidRef.current = null;
        setIsDragging(false);
        setDropTargetFileUid(null);
    }

    const endDrag = useCallback(() => {
        dragDepth.current = 0;
        rowDragDepths.current.clear();
        dropTargetFileUidRef.current = null;
        setIsDragging(false);
        setDropTargetFileUid(null);
    }, []);

    const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        dragDepth.current += 1;
        setIsDragging(true);
    }, []);

    const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) {
            endDrag();
        }
    }, [endDrag]);

    const handleDrop = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        const files = releasedFiles(event);
        const targetFileUid = dropTargetFileUidRef.current;
        endDrag();
        if (files.length === 0) {
            onEmptyDrop?.();
            return;
        }
        onFilesDropped(files, targetFileUid);
    }, [endDrag, onFilesDropped, onEmptyDrop]);

    const handleDraggerBoundaryDrop = useCallback((event: DragEvent<HTMLElement>) => {
        event.stopPropagation();
        if (!carriesFiles(event)) return;
        event.preventDefault();
        endDrag();
        if (event.dataTransfer.files.length > 0) return;
        const files = releasedFiles(event);
        if (files.length === 0) {
            onEmptyDrop?.();
            return;
        }
        onFilesDropped(files, null);
    }, [endDrag, onEmptyDrop, onFilesDropped]);

    const handleRowDragEnter = useCallback((fileUid: string, event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        const depths = rowDragDepths.current;
        depths.set(fileUid, (depths.get(fileUid) ?? 0) + 1);
        dropTargetFileUidRef.current = fileUid;
        setDropTargetFileUid(fileUid);
    }, []);

    const handleRowDragLeave = useCallback((fileUid: string, event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        const depths = rowDragDepths.current;
        const remaining = Math.max(0, (depths.get(fileUid) ?? 0) - 1);
        if (remaining > 0) {
            depths.set(fileUid, remaining);
            return;
        }
        depths.delete(fileUid);
        if (dropTargetFileUidRef.current !== fileUid) return;
        dropTargetFileUidRef.current = null;
        setDropTargetFileUid(null);
    }, []);

    if (!enabled) {
        return {
            isDragging: false,
            dropTargetFileUid: null,
            containerHandlers: {
                onDragEnter: undefined,
                onDragOver: undefined,
                onDragLeave: undefined,
                onDrop: undefined,
            },
            onDraggerBoundaryDrop: undefined,
            onRowDragEnter: undefined,
            onRowDragLeave: undefined,
        };
    }

    return {
        isDragging,
        dropTargetFileUid,
        containerHandlers: {
            onDragEnter: handleDragEnter,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        },
        onDraggerBoundaryDrop: handleDraggerBoundaryDrop,
        onRowDragEnter: handleRowDragEnter,
        onRowDragLeave: handleRowDragLeave,
    };
};
